import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { TrendingUp, TrendingDown, Layers, Pencil, Loader2, History, Activity } from "lucide-react";

const money = (v) => (v == null || isNaN(v) ? "—" : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const money4 = (v) => (v == null || isNaN(v) ? "—" : `$${Number(v).toLocaleString("en-US", { maximumFractionDigits: 4 })}`);
const pct = (v) => `${v > 0 ? "+" : ""}${Number(v || 0).toFixed(1)}%`;
const toneCls = (c) => (c > 0 ? "text-rose-600" : c < 0 ? "text-emerald-600" : "text-slate-500");

function PriceHistory({ hist }) {
  const data = (hist.history || [])
    .map((e, i) => ({ x: typeof e === "object" ? (e.date || i) : i, price: typeof e === "object" ? (e.price ?? e.close ?? e.value) : e }))
    .filter((d) => d.price != null);
  if (data.length < 2) {
    return (
      <div className="text-xs text-slate-400 py-3 text-center border border-dashed border-slate-200 rounded-lg" data-testid={`price-history-empty-${hist.index_id}`}>
        Price history will build here as <b>{hist.name}</b> changes over time.
      </div>
    );
  }
  const up = data[data.length - 1].price >= data[0].price;
  return (
    <div data-testid={`price-history-${hist.index_id}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-slate-600">{hist.name} <span className="text-slate-400">({hist.unit})</span></span>
        <span className={`text-xs font-semibold ${toneCls(hist.change_pct)}`}>{money4(hist.current_price)} · {pct(hist.change_pct)}</span>
      </div>
      <ResponsiveContainer width="100%" height={120}>
        <LineChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
          <XAxis dataKey="x" tick={{ fontSize: 9, fill: "#94a3b8" }} minTickGap={30} />
          <YAxis tick={{ fontSize: 9, fill: "#94a3b8" }} width={44} domain={["auto", "auto"]} />
          <Tooltip formatter={(v) => money4(v)} labelStyle={{ fontSize: 11 }} contentStyle={{ fontSize: 11 }} />
          <Line type="monotone" dataKey="price" stroke={up ? "#059669" : "#e11d48"} strokeWidth={2} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function CatalogItemDetail({ catalogId, onClose, onEdit, canEdit }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/catalog/${catalogId}/detail`).then((r) => { if (alive) { setData(r.data); setLoading(false); } }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [catalogId]);

  const item = data?.item;
  const isAssembly = item?.type === "assembly";
  const eff = item?.index_effect;
  const usage = data?.usage || {};

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="catalog-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isAssembly && <Layers className="w-4 h-4 text-violet-600" />}
            {item?.name || "Loading…"}
          </DialogTitle>
          <DialogDescription>
            {item ? <>{item.category || "Uncategorized"}{item.cost_code ? ` · ${item.cost_code}` : ""} · <span className="capitalize">{(item.type || "").replace("_", " ")}</span></> : "Fetching metadata…"}
          </DialogDescription>
        </DialogHeader>

        {loading || !item ? (
          <div className="py-10 text-center text-sm text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading metadata…</div>
        ) : (
          <div className="space-y-5">
            {/* Index effect */}
            <section data-testid="detail-index-effect">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5 flex items-center gap-1"><Activity className="w-3 h-3" /> Index effect</div>
              {isAssembly ? (
                eff && eff.linked_components > 0 ? (
                  <div className="rounded-lg border border-slate-200 p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Material (baseline → live)</span>
                      <span className="text-sm font-mono">
                        {money(eff.baseline_material)} → <b>{money(eff.adjusted_material)}</b>
                        <span className={`ml-2 font-semibold ${toneCls(eff.change_pct)}`}>{eff.change_pct >= 0 ? <TrendingUp className="w-3 h-3 inline" /> : <TrendingDown className="w-3 h-3 inline" />} {pct(eff.change_pct)}</span>
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1">{eff.linked_components} of {eff.total_components} components track an index{eff.indices.length ? ` · ${eff.indices.join(", ")}` : ""}</div>
                  </div>
                ) : (
                  <div className="text-xs text-slate-400 rounded-lg border border-dashed border-slate-200 p-3">No components are linked to a commodity index yet — this assembly's price won't move with the market. Link parts to an index in the editor.</div>
                )
              ) : item.commodity_index_id && item.index_name ? (
                <div className="rounded-lg border border-slate-200 p-3 flex items-center justify-between">
                  <span className="text-sm text-slate-600">{item.index_name}</span>
                  <span className="text-sm font-mono">{money4(item.unit_material_cost)} → <b>{money4(item.material_adjusted)}</b><span className={`ml-2 font-semibold ${toneCls(item.index_change_pct)}`}>{pct(item.index_change_pct)}</span></span>
                </div>
              ) : (
                <div className="text-xs text-slate-400 rounded-lg border border-dashed border-slate-200 p-3">Not linked to a commodity index — price is fixed.</div>
              )}
            </section>

            {/* Assembly component breakdown */}
            {isAssembly && (item.components || []).length > 0 && (
              <section data-testid="detail-components">
                <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Components ({item.components.length})</div>
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500"><tr>
                      <th className="text-left px-2 py-1.5">Component</th><th className="text-right px-2 py-1.5">Qty</th>
                      <th className="text-left px-2 py-1.5">Index</th><th className="text-right px-2 py-1.5">Baseline</th>
                      <th className="text-right px-2 py-1.5">Live</th><th className="text-right px-2 py-1.5">Δ</th>
                    </tr></thead>
                    <tbody>
                      {item.components.map((c, i) => {
                        const chg = c.material_line_baseline ? ((c.material_line_adjusted - c.material_line_baseline) / c.material_line_baseline * 100) : 0;
                        return (
                          <tr key={i} className="border-t border-slate-100" data-testid={`detail-component-${i}`}>
                            <td className="px-2 py-1.5 text-slate-700">{c.name || "—"}</td>
                            <td className="px-2 py-1.5 text-right font-mono">{c.qty}</td>
                            <td className="px-2 py-1.5 text-slate-500">{c.index_name || <span className="text-slate-300">none</span>}</td>
                            <td className="px-2 py-1.5 text-right font-mono">{money(c.material_line_baseline)}</td>
                            <td className="px-2 py-1.5 text-right font-mono font-semibold">{money(c.material_line_adjusted)}</td>
                            <td className={`px-2 py-1.5 text-right font-semibold ${toneCls(chg)}`}>{c.index_name ? pct(chg) : "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* Price history */}
            {Object.keys(data.price_history || {}).length > 0 && (
              <section data-testid="detail-price-history">
                <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5 flex items-center gap-1"><History className="w-3 h-3" /> Price history</div>
                <div className="space-y-3">
                  {Object.values(data.price_history).map((h) => <PriceHistory key={h.index_id} hist={h} />)}
                </div>
              </section>
            )}

            {/* Usage history */}
            <section data-testid="detail-usage">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Usage history</div>
              {usage.count > 0 ? (
                <div className="rounded-lg border border-slate-200 p-3 space-y-1.5">
                  <div className="text-sm text-slate-700">Used <b>{usage.count}×</b>{usage.last_project_name ? <> · last on <span className="font-medium">{usage.last_project_name}</span></> : ""}{usage.last_used_at ? <span className="text-slate-400"> ({new Date(usage.last_used_at).toLocaleDateString()})</span> : ""}</div>
                  {(usage.projects || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {usage.projects.slice(0, 12).map((p, i) => <span key={i} className="text-[11px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{p.name || p.id}</span>)}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-slate-400 rounded-lg border border-dashed border-slate-200 p-3">Not used on any bid yet.</div>
              )}
            </section>

            {canEdit && (
              <div className="flex justify-end pt-1">
                <Button variant="outline" onClick={() => onEdit(item)} data-testid="detail-edit-btn"><Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit item</Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
